import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { Testimonial } from '../types/testimonial';
import { ArrowLeftIcon, ArrowRightIcon, CameraIcon, PinIcon, QuoteIcon, StarIcon, StoreIcon, VerifiedIcon } from './icons/CraftIcons';
type TestimonialCarouselProps = {
  items: Testimonial[];
  tone?: 'light' | 'dark';
  labelledBy: string;
};
const metaIcon = {
  location: PinIcon,
  handle: CameraIcon,
  shop: StoreIcon
};
export function TestimonialCarousel({
  items,
  tone = 'light',
  labelledBy
}: TestimonialCarouselProps) {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const go = useCallback((dir: number) => {
    setDirection(dir);
    setIndex(prev => (prev + dir + items.length) % items.length);
  }, [items.length]);
  const jumpTo = useCallback((next: number) => {
    setDirection(next > index ? 1 : -1);
    setIndex(next);
  }, [index]);

  // Only observe / animate while the carousel is actually on screen
  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(entries => setInView(entries[0]?.isIntersecting ?? false), {
      threshold: 0.2
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (paused || !inView) return;
    const id = window.setInterval(() => go(1), 7000);
    return () => window.clearInterval(id);
  }, [go, paused, inView]);
  const active = items[index];
  const MetaIcon = metaIcon[active.metaKind];
  const isDark = tone === 'dark';
  const shell = isDark ? 'border-cream/10 bg-cream/[0.04]' : 'border-hairline bg-ivory shadow-lift';
  const quoteText = isDark ? 'text-cream/85' : 'text-ink-soft';
  const nameText = isDark ? 'text-cream' : 'text-ink';
  const metaText = isDark ? 'text-cream/55' : 'text-ink-faint';
  const navBtn = isDark ? 'border-cream/20 bg-cream/5 text-cream hover:bg-cream/10' : 'border-hairline bg-cream text-ink hover:bg-linen';
  return <div ref={rootRef} className="perspective-stage relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} role="group" aria-labelledby={labelledBy} aria-roledescription="carousel" data-id="element-406" data-mp="1p61y9fbh101p">
      <div className={`relative overflow-hidden rounded-[28px] border-2 ${shell}`} data-id="element-407" data-mp="28s7y2yoe30tb">
        <AnimatePresence mode="wait" initial={false} data-id="element-408" data-mp="28uke36al713q">
          <motion.article key={active.id} initial={{
          opacity: 0,
          x: direction * 70,
          rotateY: direction * 7
        }} animate={{
          opacity: 1,
          x: 0,
          rotateY: 0
        }} exit={{
          opacity: 0,
          x: direction * -70,
          rotateY: direction * -7
        }} transition={{
          duration: 0.5,
          ease: [0.22, 1, 0.36, 1]
        }} drag="x" dragConstraints={{
          left: 0,
          right: 0
        }} dragElastic={0.14} onDragEnd={(_event, info) => {
          if (info.offset.x < -60) go(1);
          if (info.offset.x > 60) go(-1);
        }} className="grid cursor-grab active:cursor-grabbing lg:grid-cols-[0.85fr_1.15fr]" aria-roledescription="slide" aria-label={`${index + 1} of ${items.length}`} data-id="element-409" data-mp="2csi60gvqs1at">
            {/* Photo */}
            <div className="relative h-64 overflow-hidden sm:h-80 lg:h-full lg:min-h-[420px]" data-id="element-410" data-mp="67dsajeo4bi2">
              <motion.img src={active.image} alt={active.imageAlt} className="h-full w-full object-cover" initial={{
              scale: 1.08
            }} animate={{
              scale: 1
            }} transition={{
              duration: 1.1,
              ease: 'easeOut'
            }} draggable={false} loading="lazy" decoding="async" data-id="element-411" data-mp="kvrs0lgsfjue" />
              <span className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-lg bg-ink/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-cream sm:backdrop-blur-sm" data-id="element-412" data-mp="2mqzajokbhulo">
                <CameraIcon className="h-3.5 w-3.5" data-id="element-413" data-mp="2vrnnsh1vzqxi" />
                {active.caption}
              </span>
            </div>

            {/* Quote */}
            <div className="flex flex-col justify-center gap-4 p-6 sm:p-9" data-id="element-414" data-mp="35kcy38lpayr9">
              <div className="flex items-center justify-between gap-3" data-id="element-415" data-mp="3aieflp3haq0m">
                <span className="flex items-center gap-0.5 text-gold" data-id="element-416" data-mp="3akr5qpje46or">
                  {[0, 1, 2, 3, 4].map(star => <StarIcon key={star} className="h-4 w-4 fill-current" strokeWidth={1.2} data-id="element-417" data-mp="3jjcp2mworpql" />)}
                  <span className="sr-only" data-id="element-418" data-mp="1k6qmhsmoe0xb">5 out of 5 stars</span>
                </span>
                <span className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide ${isDark ? 'bg-sage/25 text-sage-tint' : 'bg-sage-tint text-sage-dark'}`} data-id="element-419" data-mp="1p51wc3cz0bfj">
                  <VerifiedIcon className="h-3.5 w-3.5" data-id="element-420" data-mp="17vzbd94bnxm8" />
                  Verified buyer
                </span>
              </div>

              <p className={`w-fit rounded-lg px-2.5 py-1 font-display text-sm font-bold ${isDark ? 'bg-gold/15 text-gold-light' : 'bg-gold-tint text-gold-dark'}`} data-id="element-421" data-mp="12xo332todv64">
                {active.highlight}
              </p>

              <div className="relative" data-id="element-422" data-mp="1qlxuck9nrb4n">
                <QuoteIcon className={`absolute -left-1 -top-2 h-8 w-8 ${isDark ? 'text-cream/15' : 'text-hairline'}`} data-id="element-423" data-mp="1qkw7zjn6z27m" />
                <blockquote className={`relative pl-6 text-sm leading-relaxed sm:text-base ${quoteText}`} data-id="element-424" data-mp="f8ve5qbh0nl0">
                  {active.quote}
                </blockquote>
              </div>

              <footer className="flex items-center gap-3 pt-1" data-id="element-425" data-mp="ckt0ahm2yn9s">
                <span className={`flex h-10 w-10 items-center justify-center rounded-full font-display text-sm font-extrabold ${isDark ? 'bg-cream/10 text-cream' : 'bg-linen text-ink'}`} aria-hidden="true" data-id="element-426" data-mp="hknuau15jsz2">
                  {active.name.charAt(0)}
                </span>
                <div data-id="element-427" data-mp="3us8tyycb87w8">
                  <p className={`text-sm font-extrabold ${nameText}`} data-id="element-428" data-mp="3utk5xlfwslyf">{active.name}</p>
                  <p className={`flex items-center gap-1.5 text-[11px] ${metaText}`} data-id="element-429" data-mp="3g4dxd6v6ljdv">
                    <MetaIcon className="h-3.5 w-3.5" data-id="element-430" data-mp="3l2fp0g6oar0y" />
                    {active.meta}
                  </p>
                </div>
              </footer>
            </div>
          </motion.article>
        </AnimatePresence>
      </div>

      {/* Controls */}
      <div className="mt-5 flex items-center justify-between gap-4" data-id="element-431" data-mp="3b3q5yk31j0j4">
        <div className="flex gap-2" data-id="element-432" data-mp="2sh8xi4fhgf6u">
          <button type="button" onClick={() => go(-1)} aria-label="Previous review" className={`flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${navBtn}`} data-id="element-433" data-mp="2sibqqj0a2tpx">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-434" data-mp="2whjd88t2ujyw" />
          </button>
          <button type="button" onClick={() => go(1)} aria-label="Next review" className={`flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${navBtn}`} data-id="element-435" data-mp="3bf3eb4muruzs">
            <ArrowRightIcon className="h-4 w-4" data-id="element-436" data-mp="3k63q1vmzgq9v" />
          </button>
        </div>

        <div className="flex items-center gap-2" data-id="element-437" data-mp="3g5juuxlyzwqd">
          {items.map((item, itemIndex) => <button key={item.id} type="button" onClick={() => jumpTo(itemIndex)} aria-label={`Show review from ${item.name}`} aria-current={itemIndex === index} className={`h-12 w-12 overflow-hidden rounded-xl border-2 transition-all duration-300 sm:h-14 sm:w-14 ${itemIndex === index ? 'scale-105 border-gold opacity-100' : `${isDark ? 'border-cream/15' : 'border-hairline'} opacity-55 hover:opacity-90`}`} data-id="element-438" data-mp="3u4rwdx5lvgxq" data-mpi={itemIndex}>
              <img src={item.image} alt="" className="h-full w-full object-cover" draggable={false} loading="lazy" decoding="async" data-id="element-439" data-mp="gpk7n5mq8btf" />
            </button>)}
        </div>
      </div>
    </div>;
}